import React, { useState, useEffect } from "react";
import { BarChart3, Eye, ArrowUp, ArrowDown, X, TrendingUp, Award, Sparkles } from "lucide-react";
import apinode from "../../services/apinode";

// Função para agrupar os itens por apelido
const groupByApelido = (items) => {
    if (!Array.isArray(items)) {
        console.error("groupByApelido recebeu um valor inválido:", items);
        return {}; // retorna vazio para não quebrar
    }

    return items.reduce((acc, item) => {
        if (!acc[item.apelido]) {
            acc[item.apelido] = [];
        }
        acc[item.apelido].push(item);
        return acc;
    }, {});
};

// Função para comparar rankings e indicar subida ou descida
const compareRankings = (oldData, newData) => {
    const changes = {};

    Object.keys(newData).forEach((apelido) => {
        const oldRanking = oldData[apelido] || [];
        const newRanking = newData[apelido];

        newRanking.forEach((item, index) => {
            const oldIndex = oldRanking.findIndex((oldItem) => oldItem.sku === item.sku);
            if (oldIndex > index) {
                changes[item.sku] = "up";
            } else if (oldIndex < index && oldIndex !== -1) {
                changes[item.sku] = "down";
            }
        });
    });

    return changes;
};

const ItemList = () => {
    const [data, setData] = useState([]);
    const [groupedData, setGroupedData] = useState({});
    const [previousData, setPreviousData] = useState({});
    const [changes, setChanges] = useState({});
    const [show, setShow] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const storedData = localStorage.getItem("previousRanking");
        if (storedData) {
            setPreviousData(JSON.parse(storedData));
        }

        apinode.get(`/vendaspormkt/${localStorage.idconta}`)
            .then(response => {
                setData(response.data);
                const grouped = groupByApelido(response.data);
                setGroupedData(grouped);

                const newChanges = compareRankings(previousData, grouped);
                setChanges(newChanges);
                localStorage.setItem("previousRanking", JSON.stringify(grouped));

                setLoading(false);
            })
            .catch(error => {
                console.error("Erro ao buscar dados:", error);
                setLoading(false);
            });
    }, []);

    const sortByRanking = (items) => {
        return items.sort((a, b) => b.quantidade_total - a.quantidade_total);
    };

    const getRankingColor = (index) => {
        switch(index) {
            case 0: return 'linear-gradient(135deg, #FFD700, #FFA500)'; // Ouro
            case 1: return 'linear-gradient(135deg, #C0C0C0, #A9A9A9)'; // Prata
            case 2: return 'linear-gradient(135deg, #CD7F32, #8B4513)'; // Bronze
            default: return 'linear-gradient(135deg, #667eea, #764ba2)';
        }
    };

    const getRankingIcon = (index) => {
        if (index === 0) return <Award size={16} color="#FFD700" />;
        if (index === 1) return <Award size={16} color="#C0C0C0" />;
        if (index === 2) return <Award size={16} color="#CD7F32" />;
        return <span style={{fontSize: '12px', fontWeight: 'bold'}}>{index + 1}</span>;
    };

    return (
        <>
            <style>
                {`
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    @keyframes pulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                        100% { transform: scale(1); }
                    }

                    @keyframes rotate {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }

                    @keyframes shimmer {
                        0% { background-position: -200px 0; }
                        100% { background-position: calc(200px + 100%) 0; }
                    }

                    .card-hover {
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        cursor: pointer;
                    }

                    .card-hover:hover {
                        transform: translateY(-8px) scale(1.02);
                        box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
                    }

                    .ranking-item {
                        transition: all 0.2s ease;
                        animation: fadeInUp 0.5s ease forwards;
                    }

                    .ranking-item:hover {
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
                        color: white;
                        transform: scale(1.02);
                    }

                    .spinner {
                        width: 60px;
                        height: 60px;
                        border: 4px solid transparent;
                        border-top: 4px solid #667eea;
                        border-right: 4px solid #764ba2;
                        border-radius: 50%;
                        animation: rotate 1s linear infinite;
                    }

                    .shimmer-bg {
                        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                        background-size: 200px 100%;
                        animation: shimmer 1.5s infinite;
                    }

                    .glassmorphism {
                        background: rgba(255, 255, 255, 0.1);
                        backdrop-filter: blur(10px);
                        border: 1px solid rgba(255, 255, 255, 0.2);
                    }
                `}
            </style>

            <div style={{
                // background: 'rgba(255, 255, 255, 0.95)',
                // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                minHeight: '100vh',
                padding: '20px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '24px',
                    padding: '40px',
                    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(20px)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Header */}
                    <div style={{
                        textAlign: 'center',
                        marginBottom: '40px',
                        position: 'relative'
                    }}>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '12px',
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: '2.5rem',
                            fontWeight: '800',
                            marginBottom: '12px'
                        }}>
                            <TrendingUp size={40} style={{color: '#667eea'}} />
                            Top Vendas ML
                        </div>
                        <p style={{
                            color: '#64748b',
                            fontSize: '1.1rem',
                            margin: '0',
                            fontWeight: '500'
                        }}>
                            Últimos 30 dias • Rankings em tempo real
                        </p>
                        <Sparkles 
                            size={20} 
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '20px',
                                color: '#fbbf24',
                                animation: 'pulse 2s infinite'
                            }} 
                        />
                    </div>

                    {/* Ver Mais Button */}
                    {Object.keys(groupedData).length > 0 && (
                        <button
                            onClick={() => {
                                setSelectedItems(groupedData);
                                setShow(true);
                            }}
                            style={{
                                position: 'absolute',
                                top: '30px',
                                right: '30px',
                                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                border: 'none',
                                borderRadius: '50px',
                                color: 'white',
                                padding: '12px 24px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '600',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 12px 35px rgba(102, 126, 234, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.3)';
                            }}
                        >
                            <Eye size={18} />
                            Ver Tudo
                        </button>
                    )}

                    {/* Loading */}
                    {loading ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '400px',
                            gap: '20px'
                        }}>
                            <div className="spinner"></div>
                            <p style={{
                                color: '#64748b',
                                fontSize: '1.1rem',
                                fontWeight: '500'
                            }}>
                                Carregando rankings...
                            </p>
                        </div>
                    ) : (
                        /* Cards Grid */
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
                            gap: '30px'
                        }}>
                            {Object.keys(groupedData).map((apelido, cardIndex) => (
                                <div
                                    key={apelido}
                                    className="card-hover"
                                    style={{
                                        background: 'linear-gradient(145deg, #ffffff, #f8fafc)',
                                        borderRadius: '20px',
                                        overflow: 'hidden',
                                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                                        border: '1px solid rgba(255, 255, 255, 0.5)',
                                        animationDelay: `${cardIndex * 0.1}s`,
                                        animation: 'fadeInUp 0.6s ease forwards'
                                    }}
                                >
                                    {/* Card Header */}
                                    <div style={{
                                        background: `linear-gradient(135deg, ${['#667eea', '#f093fb', '#4facfe', '#43e97b'][cardIndex % 4]}, ${['#764ba2', '#f093fb', '#00f2fe', '#38f9d7'][cardIndex % 4]})`,
                                        padding: '24px',
                                        color: 'white',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}>
                                        <h3 style={{
                                            margin: '0',
                                            fontSize: '1.4rem',
                                            fontWeight: '700',
                                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                        }}>
                                            {apelido}
                                        </h3>
                                        <div style={{
                                            position: 'absolute',
                                            top: '-20px',
                                            right: '-20px',
                                            width: '80px',
                                            height: '80px',
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            borderRadius: '50%'
                                        }}></div>
                                    </div>

                                    {/* Rankings List */}
                                    <div style={{ padding: '24px' }}>
                                        {sortByRanking(groupedData[apelido]).slice(0, 3).map((item, index) => (
                                            <div
                                                key={item.sku}
                                                className="ranking-item"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '16px',
                                                    marginBottom: '12px',
                                                    borderRadius: '16px',
                                                    background: index === 0 ? 'linear-gradient(135deg, #fff7ed, #fed7aa)' :
                                                               index === 1 ? 'linear-gradient(135deg, #f8fafc, #e2e8f0)' :
                                                               'linear-gradient(135deg, #fef7f0, #fed7aa)',
                                                    border: `2px solid ${index === 0 ? '#fb923c' : index === 1 ? '#64748b' : '#f97316'}`,
                                                    animationDelay: `${index * 0.1}s`
                                                }}
                                            >
                                                {/* Ranking Badge */}
                                                <div style={{
                                                    minWidth: '50px',
                                                    height: '50px',
                                                    borderRadius: '50%',
                                                    background: getRankingColor(index),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginRight: '16px',
                                                    color: 'white',
                                                    fontWeight: 'bold',
                                                    fontSize: '1.1rem',
                                                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
                                                }}>
                                                    {getRankingIcon(index)}
                                                </div>

                                                {/* Item Info */}
                                                <div style={{ flex: 1 }}>
                                                    <div style={{
                                                        fontWeight: '600',
                                                        fontSize: '0.9rem',
                                                        color: '#1e293b',
                                                        marginBottom: '4px'
                                                    }}>
                                                        {item.sku}
                                                    </div>
                                                    <div style={{
                                                        color: '#64748b',
                                                        fontSize: '0.8rem',
                                                        lineHeight: '1.3'
                                                    }}>
                                                        {item.descricao}
                                                    </div>
                                                </div>

                                                {/* Quantity & Change */}
                                                <div style={{
                                                    textAlign: 'right',
                                                    marginLeft: '16px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '1.3rem',
                                                        fontWeight: '800',
                                                        color: '#1e293b',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        justifyContent: 'flex-end'
                                                    }}>
                                                        {item.quantidade_total}
                                                        {changes[item.sku] === "up" && (
                                                            <ArrowUp size={20} color="#10b981" style={{animation: 'pulse 1s infinite'}} />
                                                        )}
                                                        {changes[item.sku] === "down" && (
                                                            <ArrowDown size={20} color="#ef4444" style={{animation: 'pulse 1s infinite'}} />
                                                        )}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.7rem',
                                                        color: '#94a3b8',
                                                        fontWeight: '500'
                                                    }}>
                                                        unidades
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Modal */}
                    {show && (
                        <div style={{
                            position: 'fixed',
                            top: '0',
                            left: '0',
                            width: '100%',
                            height: '100%',
                            background: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(8px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000,
                            padding: '20px'
                        }} onClick={() => setShow(false)}>
                            <div
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    background: 'white',
                                    borderRadius: '24px',
                                    maxWidth: '95vw',
                                    maxHeight: '90vh',
                                    overflow: 'auto',
                                    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
                                    animation: 'fadeInUp 0.3s ease'
                                }}
                            >
                                {/* Modal Header */}
                                <div style={{
                                    padding: '30px',
                                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                    color: 'white',
                                    borderRadius: '24px 24px 0 0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <h2 style={{
                                        margin: '0',
                                        fontSize: '1.8rem',
                                        fontWeight: '700'
                                    }}>
                                        🏆 Ranking Completo
                                    </h2>
                                    <button
                                        onClick={() => setShow(false)}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.2)',
                                            border: 'none',
                                            borderRadius: '12px',
                                            color: 'white',
                                            width: '44px',
                                            height: '44px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                                            e.target.style.transform = 'scale(1.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                                            e.target.style.transform = 'scale(1)';
                                        }}
                                    >
                                        <X size={20} />
                                    </button>
                                </div>

                                {/* Modal Body */}
                                <div style={{
                                    padding: '30px',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
                                    gap: '30px',
                                    maxHeight: '60vh',
                                    overflow: 'auto'
                                }}>
                                    {Object.keys(selectedItems).map((apelido, cardIndex) => (
                                        <div key={apelido} style={{
                                            background: 'linear-gradient(145deg, #ffffff, #f8fafc)',
                                            borderRadius: '16px',
                                            overflow: 'hidden',
                                            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)'
                                        }}>
                                            <div style={{
                                                background: `linear-gradient(135deg, ${['#667eea', '#f093fb', '#4facfe', '#43e97b'][cardIndex % 4]}, ${['#764ba2', '#f093fb', '#00f2fe', '#38f9d7'][cardIndex % 4]})`,
                                                padding: '20px',
                                                color: 'white',
                                                textAlign: 'center'
                                            }}>
                                                <h3 style={{ margin: '0', fontWeight: '700' }}>{apelido}</h3>
                                            </div>
                                            <div style={{ padding: '20px', maxHeight: '400px', overflow: 'auto' }}>
                                                {sortByRanking(selectedItems[apelido]).slice(0, 50).map((item, index) => (
                                                    <div key={item.sku} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '12px',
                                                        marginBottom: '8px',
                                                        borderRadius: '12px',
                                                        background: index < 3 ? 'linear-gradient(135deg, #fef3c7, #fde68a)' : '#f8fafc',
                                                        border: index < 3 ? '1px solid #f59e0b' : '1px solid #e2e8f0',
                                                        fontSize: '0.85rem'
                                                    }}>
                                                        <div style={{
                                                            minWidth: '35px',
                                                            height: '35px',
                                                            borderRadius: '50%',
                                                            background: index < 3 ? getRankingColor(index) : '#64748b',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            marginRight: '12px',
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: '0.8rem'
                                                        }}>
                                                            {index < 3 ? getRankingIcon(index) : index + 1}
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: '600', color: '#1e293b' }}>
                                                                {item.sku}
                                                            </div>
                                                            <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                                                                {item.descricao}
                                                            </div>
                                                        </div>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            fontWeight: '700',
                                                            color: '#1e293b'
                                                        }}>
                                                            {item.quantidade_total}
                                                            {changes[item.sku] === "up" && <ArrowUp size={14} color="#10b981" />}
                                                            {changes[item.sku] === "down" && <ArrowDown size={14} color="#ef4444" />}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ItemList;